import FullWidthCard from 'Components/common/Cards/FullWidthCard/FullWidthCard'
import styles from '../Home.module.less'
import { useNavigate } from 'react-router-dom'
import { useUserProfile } from 'shared/auth/hooks/use-user-profile/useUserProfile'
import { isUserAssociatedWithProject, projectTimeSheetEntries } from 'Services/projects.service'
import { EmptyStateIndicator } from 'Components/common/EmptyStaleIndicator/EmptyStaleIndicator'
import { Loader } from 'Components/common/Loader/Loader'
import { Flex } from 'antd/lib'
import { useQuery } from '@tanstack/react-query'
import { META_PROJECT_ID } from 'Constants/constants'
import TableComp from 'Components/common/Table/Table'
import { useMemo } from 'react'
import { useMessage } from 'shared/hooks/use-message'

export const TimesheetTab = () => {
    const navigate = useNavigate()
    const { userProfile } = useUserProfile()
    const [messageApi] = useMessage()

    const {
        data: userAssociationData,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['userProjectAssociation', META_PROJECT_ID, userProfile.user.user_id],
        queryFn: () =>
            isUserAssociatedWithProject({
                projectId: META_PROJECT_ID,
                userId: userProfile.user.user_id,
            }),
        enabled: !!userProfile.user.user_id,
        staleTime: 3 * 60 * 1000,
    })

    const isUserAssociated = userAssociationData?.is_user_associated ?? false

    const {
        data: entriesData,
        isLoading: isTimeSheetLoading,
        isError: isTimeSheetError,
    } = useQuery({
        queryKey: ['timeSheetEntries', META_PROJECT_ID, userProfile.user.user_id],
        queryFn: () =>
            projectTimeSheetEntries({
                projectId: META_PROJECT_ID,
                userId: userProfile.user.user_id,
            }),
        enabled: !!userProfile.user.user_id,
    })

    const columns = useMemo(
        () => [
            {
                title: 'Date',
                dataIndex: 'task_solved_date',
                key: 'task_solved_date',
                className: styles['feedback-td'],
            },
            {
                title: 'Platform',
                dataIndex: 'platform',
                key: 'platform',
                className: styles['feedback-td'],
            },
            {
                title: 'Completed Tasks',
                dataIndex: 'completed_tasks',
                key: 'completed_tasks',
                className: styles['feedback-td'],
            },
            {
                title: 'Time Spent',
                dataIndex: 'time',
                key: 'time',
                className: styles['feedback-td'],
            },
            {
                title: 'Comments',
                dataIndex: 'comments',
                key: 'comments',
                className: styles['feedback-td'],
            },
        ],
        []
    )

    const entries = useMemo(() => {
        if (isTimeSheetError) {
            return []
        }
        return (
            entriesData?.timesheet_entries?.map(entry => ({
                key: entry.id,
                task_solved_date: new Date(entry.form_value.task_solved_date).toLocaleDateString(
                    'en-US',
                    { timeZone: 'UTC' }
                ),
                platform: entry.form_value.platform,
                completed_tasks: entry.form_value.completed_tasks,
                time: entry.form_value.time,
                comments: entry.form_value.comments || '-',
            })) || []
        )
    }, [entriesData, isTimeSheetError])

    if (error) {
        console.error(error)
        messageApi.error('Failed to check user associated with project')
    }

    if (isLoading) {
        return <Loader title="Loading please wait..." />
    }

    return (
        <div className={styles['timesheet']}>
            {isUserAssociated ? (
                <div className={styles['timesheet-tab-container']}>
                    <FullWidthCard
                        title="Project Sonic"
                        tags={[{ title: 'Production Time Logger', type: 'primary' }]}
                        onClick={() => {
                            navigate(`/timesheet/${META_PROJECT_ID}`, {
                                state: { timesheet_authorized: isUserAssociated },
                            })
                        }}
                        content="Make sure you submit only once per day. If you're from another project, please ignore this."
                    />
                    <div className={styles['timesheet-tab-container_table-container']}>
                        <div className={styles['timesheet-tab-container_heading']}>
                            Your Entries
                        </div>
                        <TableComp
                            loading={isTimeSheetLoading}
                            columns={columns}
                            dataSource={entries}
                            pagination={false}
                            scroll={{ y: 400 }}
                            emptyIconText="No Entries found"
                        />
                    </div>
                    <Flex
                        className={styles['timesheet-tab-container_email-link']}
                        justify="flex-end"
                        align="center"
                        gap={4}
                    >
                        *Contact project team via
                        <a
                            href="mailto:community_support@telusinternational.ai"
                            className={styles['timesheet-tab-container_email-link']}
                        >
                            community_support@telusinternational.ai
                        </a>{' '}
                        for any issues.
                    </Flex>
                </div>
            ) : (
                <EmptyStateIndicator caption="This is not applicable to you" />
            )}
        </div>
    )
}
